import { useQuery } from '@tanstack/react-query';
import type { ModerationIntelligence } from '../types';
import { fetchModerationIntelligence, MODERATION_INTELLIGENCE_KEY } from '../api';
import { isDeepScanSettled, isProvisionalNoAction, isTerminalCaseStatus } from '../lib/verdictDisplay';

export interface UseModerationIntelligenceResult {
  intelligence: ModerationIntelligence | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * Fetch Coop intelligence data for a moderation report. Auto-refreshes every 3 seconds while
 * the case is still mid-pipeline (including provisional NO_ACTION snapshots).
 */
export function useModerationIntelligence(reportId: string): UseModerationIntelligenceResult {
  const id = Number(reportId);
  const enabled = !!reportId && Number.isFinite(id) && id > 0;

  const query = useQuery({
    queryKey: MODERATION_INTELLIGENCE_KEY(id),
    queryFn: () => fetchModerationIntelligence(id),
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const deepScanSettled = isDeepScanSettled({
        deepScanDurationMs: data.deep_scan_duration_ms,
        deliveryStatus: data.delivery_status,
        vectors: data.vectors,
      });
      if (isProvisionalNoAction(data.verdict, data.case_status, { deepScanSettled })) return 3000;
      if (data.case_status && !isTerminalCaseStatus(data.case_status)) return 3000;
      return false;
    },
  });

  return {
    intelligence: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  };
}
